/**
 * ValidationRulesBlock — editor dinámico de reglas de validación
 * numéricas por sección (Doc 24 §3 Fase 9).
 *
 * Sustituye al `ValidationsEditor` legacy que renderaba slots fijos
 * (min_length/max_length grid). El v2 expone "add/remove rule" con un
 * dropdown filtrado por tipo de sección — el author elige qué reglas
 * declarar y solo paga la carga visual de las que usa.
 *
 * Catálogo soportado (8 reglas):
 *
 *   - rich_text / plain_text: min_length, max_length
 *   - bullet_list / numbered_list / checklist:
 *       min_items, max_items, min_item_length, max_item_length
 *   - key_value_grid: min_items, max_items
 *   - structured_table: min_rows, max_rows
 *
 * Cualquier regla pre-existente fuera de este catálogo (p.ej. `pattern`,
 * `item_pattern`, `regex`) NO se edita aquí pero SE PRESERVA en el
 * array — el componente las pasa intactas al `onChange` para no
 * destruir datos del legacy. Un editor v2 separado para regex / patrones
 * podría agregarse en el futuro.
 *
 * Validaciones inline:
 *   - No permite duplicados (dropdown solo ofrece reglas no-usadas).
 *   - Warning visual cuando `min_X > max_X` para el mismo dominio
 *     (length / items / item_length / rows).
 */

import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { BlockType, ValidationRule } from '../../../api/templates'

/** Nombres canónicos de las 8 reglas numéricas editables en v2.
 * Backend Pydantic exige `ge=1` en los valores numéricos. */
export type NumericRuleName =
  | 'min_length'
  | 'max_length'
  | 'min_items'
  | 'max_items'
  | 'min_item_length'
  | 'max_item_length'
  | 'min_rows'
  | 'max_rows'

const NUMERIC_RULES: ReadonlySet<NumericRuleName> = new Set([
  'min_length',
  'max_length',
  'min_items',
  'max_items',
  'min_item_length',
  'max_item_length',
  'min_rows',
  'max_rows',
])

/** Tabla de qué reglas numéricas aplica por tipo de sección. Las que no
 * estén en este map (`fixed_heading`, `mermaid_*`, `code_block`, etc.)
 * no muestran el bloque. */
const RULES_BY_BLOCK_TYPE: Partial<Record<BlockType, NumericRuleName[]>> = {
  rich_text: ['min_length', 'max_length'],
  plain_text: ['min_length', 'max_length'],
  bullet_list: ['min_items', 'max_items', 'min_item_length', 'max_item_length'],
  numbered_list: ['min_items', 'max_items', 'min_item_length', 'max_item_length'],
  checklist: ['min_items', 'max_items', 'min_item_length', 'max_item_length'],
  key_value_grid: ['min_items', 'max_items'],
  structured_table: ['min_rows', 'max_rows'],
}

/** Pares min/max que dispararán warning si `min > max`. */
const MIN_MAX_PAIRS: ReadonlyArray<[NumericRuleName, NumericRuleName]> = [
  ['min_length', 'max_length'],
  ['min_items', 'max_items'],
  ['min_item_length', 'max_item_length'],
  ['min_rows', 'max_rows'],
]

export interface ValidationRulesBlockProps {
  blockType: BlockType
  /** Reglas actuales completas — incluye numéricas Y no-numéricas (p.ej.
   * pattern). El componente solo edita las numéricas; las demás se
   * preservan intactas. */
  validations: ValidationRule[]
  readOnly?: boolean
  onChange(next: ValidationRule[]): void
}

function isNumericRule(rule: string): rule is NumericRuleName {
  return NUMERIC_RULES.has(rule as NumericRuleName)
}

function getNumericValue(
  rules: ValidationRule[],
  name: NumericRuleName,
): number | undefined {
  const found = rules.find((r) => r.rule === name)
  if (!found) return undefined
  const n = Number(found.value)
  return Number.isFinite(n) ? n : undefined
}

export function ValidationRulesBlock({
  blockType,
  validations,
  readOnly = false,
  onChange,
}: ValidationRulesBlockProps) {
  const { t } = useTranslation('templates')
  // Estado local: qué regla está pre-seleccionada en el botón "Agregar".
  // useState va ANTES del early-return para cumplir con rules-of-hooks.
  const [pendingName, setPendingName] = useState<NumericRuleName | null>(null)

  const allowed = RULES_BY_BLOCK_TYPE[blockType]

  // El bloque no se monta para tipos que no admiten reglas numéricas
  // (fixed_heading, mermaid_*, etc.) — render nulo.
  if (!allowed) return null

  // Separar reglas en "mías" (numéricas) y "pre-existentes ajenas"
  // (pattern, etc.) para preservar el array a través de onChange.
  const ownedRules = validations.filter(
    (r): r is ValidationRule & { rule: NumericRuleName } =>
      isNumericRule(r.rule),
  )
  const passthroughRules = validations.filter((r) => !isNumericRule(r.rule))
  const usedNames = new Set<NumericRuleName>(ownedRules.map((r) => r.rule))
  const remainingNames = allowed.filter((n) => !usedNames.has(n))

  function emitNext(nextOwned: ValidationRule[]) {
    onChange([...passthroughRules, ...nextOwned])
  }

  function addRule(name: NumericRuleName) {
    if (usedNames.has(name)) return
    emitNext([...ownedRules, { rule: name, value: 1 }])
    setPendingName(null)
  }

  function updateRuleValue(name: NumericRuleName, raw: string) {
    const parsed = parsePositiveInt(raw)
    const next = ownedRules.map((r) =>
      r.rule === name ? { rule: r.rule, value: parsed ?? 1 } : r,
    )
    emitNext(next)
  }

  function removeRule(name: NumericRuleName) {
    emitNext(ownedRules.filter((r) => r.rule !== name))
  }

  // Detectar warnings min > max por par.
  const warnings = new Set<NumericRuleName>()
  for (const [minName, maxName] of MIN_MAX_PAIRS) {
    const minV = getNumericValue(ownedRules, minName)
    const maxV = getNumericValue(ownedRules, maxName)
    if (minV !== undefined && maxV !== undefined && minV > maxV) {
      warnings.add(minName)
      warnings.add(maxName)
    }
  }

  return (
    <div data-testid="validation-rules-block">
      <p className="text-xs font-medium text-ink-muted mb-2">
        {t('designer.editor.validation_v2.block_title')}
      </p>

      {ownedRules.length === 0 ? (
        <p
          className="text-[11px] text-ink-subtle italic mb-2"
          data-testid="validation-rules-empty"
        >
          {t('designer.editor.validation_no_rules_yet')}
        </p>
      ) : (
        <ul className="space-y-1.5 mb-2">
          {ownedRules.map((r) => {
            const hasWarning = warnings.has(r.rule)
            return (
              <li
                key={r.rule}
                className="flex items-center gap-2"
                data-testid={`validation-rules-row-${r.rule}`}
              >
                <span className="flex-1 text-xs text-ink-muted">
                  {t(`designer.editor.validation_v2.rules.${r.rule}`)}
                </span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={typeof r.value === 'number' ? r.value : ''}
                  onChange={(e) => updateRuleValue(r.rule, e.target.value)}
                  disabled={readOnly}
                  data-testid={`validation-rules-input-${r.rule}`}
                  className={
                    hasWarning
                      ? 'w-24 bg-canvas border border-critical/60 rounded-md px-2 py-1 text-sm font-mono focus:outline-none focus:border-critical disabled:opacity-60'
                      : 'w-24 bg-canvas border border-line rounded-md px-2 py-1 text-sm font-mono focus:outline-none focus:border-brand disabled:opacity-60'
                  }
                />
                {!readOnly ? (
                  <button
                    type="button"
                    onClick={() => removeRule(r.rule)}
                    aria-label={
                      t('designer.editor.list_input_remove_item') as string
                    }
                    className="text-ink-subtle hover:text-critical p-1"
                    data-testid={`validation-rules-remove-${r.rule}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}

      {warnings.size > 0 ? (
        <p
          className="text-[11px] text-critical mb-2"
          data-testid="validation-rules-min-max-warning"
        >
          {t('designer.editor.validation_v2.min_greater_than_max_warning')}
        </p>
      ) : null}

      {!readOnly && remainingNames.length > 0 ? (
        <div className="flex items-center gap-2">
          <select
            value={pendingName ?? ''}
            onChange={(e) => {
              const v = e.target.value
              setPendingName(v === '' ? null : (v as NumericRuleName))
            }}
            data-testid="validation-rules-add-select"
            className="bg-canvas border border-line rounded-md px-2 py-1 text-xs focus:outline-none focus:border-brand"
          >
            <option value="">
              {t('designer.editor.validation_v2.select_rule_placeholder')}
            </option>
            {remainingNames.map((n) => (
              <option key={n} value={n}>
                {t(`designer.editor.validation_v2.rules.${n}`)}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={pendingName === null}
            onClick={() => {
              if (pendingName) addRule(pendingName)
            }}
            data-testid="validation-rules-add-button"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-line text-ink hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" />
            {t('designer.editor.validation_v2.add_rule')}
          </button>
        </div>
      ) : null}
    </div>
  )
}

/** Parser numérico — duplicado de `parsePositiveInt` en SectionEditorPane
 * para no acoplar a ese módulo (el block es autónomo). Devuelve `undefined`
 * para valores no-positivos / vacíos / no-enteros, lo que es interpretado
 * como "regla recién creada, valor pendiente" — sustituido por 1 al
 * persistir para cumplir `ge=1` del backend Pydantic. */
function parsePositiveInt(raw: string): number | undefined {
  const trimmed = raw.trim()
  if (!trimmed) return undefined
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 1 || !Number.isInteger(n)) return undefined
  return n
}
